import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Requirement, RequirementStatus } from '../types';
import {
  Section,
  SectionHeading,
  Button,
  BatchApiStatusHandler,
  BlockAlert,
  AlertVariants,
} from '../components';
import { Renderer, formSpecToRendererConfig } from '../components/forms';
import { useBatchApi } from '../hooks';
import { cleanIntakeDates } from '../utils/intake';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus } from '../utils/types';
import { useSearchParams } from 'react-router-dom';
import { safelyUpdateLocation } from '../utils/redirect';

interface DatumsImportProps {
  requirement: Requirement;
}

export const DatumsImport = ({ requirement }: DatumsImportProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const formFields = requirement.produces.map((ra) => {
    return {
      name: ra.attribute.id,
      label: ra.attribute.name,
      description: ra.attribute.description,
      required: ra.attribute.required ? 'This is required' : undefined,
      component: {
        type: ra.attribute.type === 'boolean' ? 'check' : ra.attribute.type,
      },
    };
  });

  /* eslint-disable camelcase */
  const loadedSpec = {
    title: `Missing Data: ${requirement.title}`,
    sections: [{ form_fields: formFields }],
  };
  /* eslint-enable camelcase */

  const formMethods = useForm();
  const {
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
  } = formMethods;

  const onSubmit = async (data: any) => {
    clearErrors();

    if (errors.isNotEmpty) {
      return;
    }

    /* eslint-disable camelcase */
    const body = Object.entries(cleanIntakeDates(data)).map(([k, v]) => {
      return { attribute_id: k, value: v };
    });
    /* eslint-enable camelcase */

    const datumResponse = await fetchApi({
      url: '/api/datums',
      method: 'POST',
      body,
    });

    if (datumResponse.status === ApiStatus.Error) {
      setError('submit', {
        type: 'server-error',
        message: "Something went wrong, we're looking into it.",
      });
      setIsLoading(false);

      return;
    }

    const reqPatchResponse = await fetchApi({
      url: `/api/requirements/${requirement.id}`,
      method: 'PUT',
      body: { status: RequirementStatus.Done },
    });

    if (reqPatchResponse.status === ApiStatus.Error) {
      setError('submit', {
        type: 'server-error',
        message: "Something went wrong, we're looking into it.",
      });
      setIsLoading(false);

      return;
    }

    if (callbackUrl) {
      safelyUpdateLocation(callbackUrl);
    }
  };

  return (
    <Section className="flex flex-col items-center">
      <SectionHeading className="w-96 pb-4" text={loadedSpec.title} />

      <FormProvider {...formMethods}>
        <form className="my-8 w-96" onSubmit={handleSubmit(onSubmit)}>
          <Renderer
            config={formSpecToRendererConfig(loadedSpec)}
            errors={errors}
          />

          <div className="space-y-4">
            {errors.submit && (
              <BlockAlert variant={AlertVariants.Error}>
                {typeof errors.submit.message === 'string'
                  ? errors.submit.message
                  : 'Something went wrong.'}
              </BlockAlert>
            )}

            <Button
              type="submit"
              size="large"
              isLoading={isLoading}
              isFullWidth
            >
              Submit
            </Button>
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export const DatumsImportView = () => {
  const { requirementId } = useParams();
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  if (!requirementId) {
    if (callbackUrl) {
      safelyUpdateLocation(callbackUrl);
    }

    throw Error('Requirement and callback URL missing.');
  }

  const batchResponse = useBatchApi([
    {
      url: `/api/requirements/${requirementId}`,
      method: 'POST',
    },
  ]);

  const componentPropsFn = ([reqResponse]: any[]): DatumsImportProps => {
    return {
      requirement: reqResponse,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={DatumsImport}
      componentProps={componentPropsFn}
    />
  );
};
